/**
 * @file NotePickerSheet.tsx
 * @brief Pick ONE of your notes — used where a note is an input, not a link.
 *
 * @details
 * Meeting Setup needs a note chosen *before* the meeting exists, so nothing can
 * be attached yet: this picker only returns the choice, and the caller attaches
 * once there is a meeting to attach to. That is the same order mobile uses.
 *
 * 01-10-2026: `multiple` mode (DES: the Boardroom setup agenda picker is
 * multi-select). Rows toggle with a tick and the menu stays open; the
 * single-pick mode, used by the recorder's agenda panel, is unchanged.
 *
 * Agendas only (DES 12-10-2026, the phone's PrepNotePickerModal): notes and
 * agendas are different things, and both callers pick a meeting's agenda, so
 * the list is `kind=agenda`. `onWriteNew` adds "Write new agenda" at the top;
 * Setup passes it, the recorder does not (the agenda is read only during a
 * recording, by decision).
 *
 * Distinct from `MeetingNotePicker`, which attaches immediately because there
 * the meeting is already real.
 *
 * @dependencies
 * - components/ui/PickerSheet
 * - features/notes/api: useNotesList
 *
 * @author DES
 * @date 21-08-2026
 */

import { useMemo, type ReactElement } from "react";
import { Plus } from "lucide-react";
import { DropdownMenuItem } from "@/components/ui/DropdownMenu";
import { toast } from "@/shared/hooks/useToast";
import { PickerSheet, type PickerSheetItem } from "@/components/ui/PickerSheet";
import { formatDateTime } from "@/shared/utils/formatters";
import { useNotesList } from "./api";
import { notePoints } from "./schemas";

export type PickedNote = { id: string; title: string };

export function NotePickerSheet({
  open,
  onOpenChange,
  selectedId,
  onPick,
  excludeIds,
  anchor,
  multiple = false,
  selectedIds,
  onToggle,
  max,
  onWriteNew,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedId: string | null;
  /** `null` when the choice is cleared. */
  onPick: (note: PickedNote | null) => void;
  /** Notes to leave out, e.g. agendas already chosen (30-09-2026). */
  excludeIds?: string[];
  /** The button it opens under, as a menu (30-09-2026). */
  anchor?: ReactElement;
  /** Multi-select: rows toggle via `onToggle`, ticked by `selectedIds` (01-10-2026). */
  multiple?: boolean;
  selectedIds?: string[];
  onToggle?: (note: PickedNote) => void;
  /** Multi-select cap: once reached, only ticked rows can be toggled. */
  max?: number;
  /** "Write new agenda" at the top (DES 12-10-2026). Setup only. */
  onWriteNew?: () => void;
}) {
  // Agendas only (DES 12-10-2026): notes and agendas are different things.
  const notes = useNotesList({ page: 1, pageSize: 50, kind: "agenda" });

  const writeNew = onWriteNew ? (
    <DropdownMenuItem
      onSelect={onWriteNew}
      // Centred, as the phone (DES 14-10-2026, "center karo").
      className="mb-1 justify-center font-semibold text-brand focus:text-brand"
    >
      <Plus className="h-4 w-4 text-brand" />
      Write new agenda
    </DropdownMenuItem>
  ) : undefined;

  const items: PickerSheetItem[] = useMemo(
    () =>
      (notes.data?.items ?? [])
        .filter((n) => !excludeIds?.includes(n.id))
        .map((n) => {
          const points = notePoints(n);
          // Points say what the note contains, whichever kind it is: ADR-025
          // gave a recording points of its own, so the old typed-only branch
          // showed a transcript slice where its points belong. The slice remains
          // for a recording still being read, which has no points yet.
          const gist =
            points.length > 0 ? points.join(" · ") : (n.transcript_text ?? "").slice(0, 120);
          return {
            id: n.id,
            label: n.title?.replace(/\s+/g, " ").trim() || "Untitled agenda",
            subtitle: `${formatDateTime(n.created_at)}${gist ? ` · ${gist}` : ""}`,
          };
        }),
    [notes.data, excludeIds],
  );

  if (multiple) {
    const count = selectedIds?.length ?? 0;
    return (
      <PickerSheet
        anchor={anchor}
        open={open}
        onOpenChange={onOpenChange}
        title={max ? `Choose agendas (${count}/${max})` : "Choose agendas"}
        subtitle={
          max
            ? `Pick up to ${max} agendas to check off afterwards`
            : "Attach an agenda to check off afterwards"
        }
        searchPlaceholder="Search your agendas"
        items={items}
        multiSelect
        selectedIds={selectedIds}
        loading={notes.isLoading}
        clearable={false}
        emptyText="You have no agendas yet."
        menuAction={writeNew}
        onSelect={(item) => {
          const ticked = selectedIds?.includes(item.id) ?? false;
          if (!ticked && max != null && count >= max) {
            toast({
              title: "Agenda is full",
              description: `A meeting takes up to ${max} agendas. Remove one first.`,
            });
            return;
          }
          onToggle?.({ id: item.id, title: item.label });
        }}
      />
    );
  }

  return (
    <PickerSheet
      anchor={anchor}
      open={open}
      onOpenChange={onOpenChange}
      title="Choose an agenda"
      subtitle="Attach an agenda to check off afterwards"
      searchPlaceholder="Search your agendas"
      items={items}
      selectedId={selectedId}
      loading={notes.isLoading}
      onClear={() => onPick(null)}
      emptyText="You have no agendas yet."
      menuAction={writeNew}
      onSelect={(item) => {
        onPick({ id: item.id, title: item.label });
        onOpenChange(false);
      }}
    />
  );
}

export default NotePickerSheet;
